/**
 * Provider-specific voice / speaking style options for narration TTS.
 * Shown beside the audio provider selector when a concrete provider is chosen.
 */

import { languageToMurfLocale, murfStylesForVoice } from '@/lib/murfVoices';
import { MURF_SPEAKING_STYLES, murfSpeakingStyleOptions } from '@/lib/murfApi';
import { NARRATION_VOICE_STYLE_OPTIONS } from '@/lib/narrationAudioSettings';

export const MINIMAX_VOICE_STYLE_OPTIONS = [
  { value: 'storyteller', label: 'Storyteller' },
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'child', label: 'Child' },
  { value: 'newscaster', label: 'Newscaster' },
];

export const ELEVENLABS_VOICE_STYLE_OPTIONS = [
  { value: 'storyteller', label: 'Storyteller (preset)' },
  { value: 'female', label: 'Female (preset)' },
  { value: 'male', label: 'Male (preset)' },
  { value: 'child', label: 'Child (preset)' },
  { value: 'newscaster', label: 'Newscaster (preset)' },
];

export const EDGE_VOICE_STYLE_OPTIONS = [
  { value: 'storyteller', label: 'Storyteller' },
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'child', label: 'Child' },
  { value: 'newscaster', label: 'Newscaster' },
];

/** Fallback Murf styles when voice catalog is not loaded yet (matches Murf API names). */
export const MURF_FALLBACK_STYLE_OPTIONS = murfSpeakingStyleOptions(MURF_SPEAKING_STYLES);

const STATIC_BY_PROVIDER = {
  minimax: MINIMAX_VOICE_STYLE_OPTIONS,
  elevenlabs: ELEVENLABS_VOICE_STYLE_OPTIONS,
  murf: MURF_FALLBACK_STYLE_OPTIONS,
  edge: EDGE_VOICE_STYLE_OPTIONS,
  'edge-tts': EDGE_VOICE_STYLE_OPTIONS,
};

const DEFAULT_BY_PROVIDER = {
  minimax: 'storyteller',
  elevenlabs: 'storyteller',
  murf: 'Promo',
  edge: 'storyteller',
  'edge-tts': 'storyteller',
  runway: 'storyteller',
};

const LABEL_BY_PROVIDER = {
  minimax: 'MiniMax style',
  elevenlabs: 'ElevenLabs preset',
  murf: 'Murf speaking style',
  edge: 'Edge voice style',
  'edge-tts': 'Edge voice style',
  runway: 'Runway preset',
};

export function shouldShowProviderStyleSelect(provider) {
  const p = String(provider || 'auto').trim().toLowerCase();
  return Boolean(p && p !== 'auto');
}

export function providerStyleFieldLabel(provider) {
  const p = String(provider || '').trim().toLowerCase();
  return LABEL_BY_PROVIDER[p] || 'Voice style';
}

export function defaultVoiceStyleForProvider(provider) {
  const p = String(provider || 'auto').trim().toLowerCase();
  return DEFAULT_BY_PROVIDER[p] || 'storyteller';
}

export function voiceStyleOptionsForTtsProvider(
  provider,
  {
    murfVoices = [],
    murfVoiceId = '',
    murfLocale = '',
    language = 'en',
  } = {},
) {
  const p = String(provider || 'auto').trim().toLowerCase();
  if (!shouldShowProviderStyleSelect(p)) {
    return NARRATION_VOICE_STYLE_OPTIONS;
  }
  if (p === 'murf') {
    const voiceId = String(murfVoiceId || '').trim();
    if (voiceId && murfVoices?.length) {
      const locale = murfLocale || languageToMurfLocale(language);
      const voice = murfVoices.find((v) => (v.voice_id || v.id) === voiceId);
      const dynamic = murfStylesForVoice(voice, locale);
      if (dynamic.length) {
        return dynamic.map((s) => ({ value: s.id, label: s.label }));
      }
    }
    return MURF_FALLBACK_STYLE_OPTIONS;
  }
  return STATIC_BY_PROVIDER[p] || NARRATION_VOICE_STYLE_OPTIONS;
}

export function normalizeVoiceStyleForProvider(provider, style, options = {}) {
  const choices = voiceStyleOptionsForTtsProvider(provider, options);
  const values = choices.map((o) => o.value);
  const current = String(style || '').trim();
  if (current && values.includes(current)) return current;
  return choices[0]?.value || defaultVoiceStyleForProvider(provider);
}
